import { useEffect, useRef, useState } from "react";
import { Truck, Loader2, MapPin, CheckCircle2, AlertCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { getDelivery, setDeliveryCheck } from "@/lib/delivery";
import { FreightQuote } from "./FreightQuote";

const km = (r) => `${r.distance_km} km${r.by_road ? " pela estrada" : ""}`;

const Result = ({ r }) => (r.available ? (
  <p data-testid="delivery-result" data-available="true" className="mt-3 flex items-start gap-2 rounded-2xl bg-emerald-400/10 px-4 py-3 text-sm text-emerald-200">
    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
    <span>Entregamos em <strong className="text-white">{r.city.name} - {r.city.uf}</strong> sem taxa de entrega. Fica a cerca de {km(r)} da loja.</span>
  </p>
) : (
  <p data-testid="delivery-result" data-available="false" className="mt-3 flex items-start gap-2 rounded-2xl bg-amber-400/10 px-4 py-3 text-sm text-amber-200">
    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
    <span><strong className="text-white">{r.city.name} - {r.city.uf}</strong> fica a cerca de {km(r)}. {r.message}</span>
  </p>
));

// Consulta de entrega na página do aparelho: cidade (com sugestões) ou CEP; o servidor calcula a distância pela estrada.
// Fora do raio, o cliente calcula o frete dos Correios para este aparelho.
export const DeliveryCheck = ({ item }) => {
  const [settings, setSettings] = useState(null);
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const picked = useRef(false);

  const run = async (params, cep = "") => {
    setBusy(true);
    setError("");
    setOptions([]);
    try {
      const { data } = await api.get("/delivery/check", { params });
      setResult(data?.city ? data : null);
      if (data?.city) setDeliveryCheck(data, cep);
    } catch (e) {
      setResult(null);
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    api.get("/delivery/settings").then((r) => setSettings(r?.data ?? null)).catch(() => setSettings(null));
    const saved = getDelivery()?.check?.city?.id;
    if (saved) run({ city: saved });
  }, []);

  useEffect(() => {
    if (picked.current) { picked.current = false; return undefined; }
    if (q.trim().length < 2 || /^\d/.test(q.trim())) { setOptions([]); return undefined; }
    const t = setTimeout(() => {
      api.get("/delivery/cities", { params: { q: q.trim() } }).then((r) => setOptions(Array.isArray(r?.data) ? r.data : [])).catch(() => setOptions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const pick = (c) => {
    picked.current = true;
    setQ(`${c.name} - ${c.uf}`);
    run({ city: c.id });
  };
  const submit = (e) => {
    e.preventDefault();
    const digits = q.replace(/\D/g, "");
    if (/^\d/.test(q.trim())) return run({ cep: digits }, digits);
    if (options[0]) return pick(options[0]);
    return setError("Escolha sua cidade na lista ou digite o CEP.");
  };

  return (
    <div data-testid="delivery-check" className="mt-6 rounded-[22px] border border-white/10 bg-white/[0.03] p-4 sm:p-5">
      <p className="flex items-center gap-2 text-sm font-semibold text-white"><Truck className="h-4 w-4 text-emerald-300" /> Entrega grátis {settings ? `em até ${settings.radius_km} km de Areado - MG` : "na região de Areado - MG"}</p>
      <p className="mt-1 text-xs text-zinc-400">Veja se entregamos na sua cidade sem taxa.</p>
      <form onSubmit={submit} className="relative mt-3 flex gap-2">
        <div className="relative min-w-0 flex-1">
          <MapPin className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input data-testid="delivery-input" value={q} maxLength={60} onChange={(e) => setQ(e.target.value)} placeholder="Sua cidade ou CEP" autoComplete="off"
            className="w-full rounded-full border border-white/10 bg-white/5 py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-white/40" />
          {options.length > 0 && (
            <ul data-testid="delivery-options" className="absolute left-0 right-0 top-full z-20 mt-2 max-h-64 overflow-auto rounded-2xl border border-white/10 bg-[#141416] p-1 shadow-2xl">
              {options.map((c) => (
                <li key={c.id}>
                  <button type="button" data-testid={`delivery-option-${c.id}`} onClick={() => pick(c)} className="w-full rounded-xl px-3 py-2 text-left text-sm text-zinc-200 hover:bg-white/10">{c.name} <span className="text-zinc-400">- {c.uf}</span></button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button data-testid="delivery-submit" disabled={busy} className="flex shrink-0 items-center justify-center rounded-full bg-white px-5 text-sm font-semibold text-black transition-transform hover:scale-[1.03] active:scale-95 disabled:opacity-70">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Consultar"}
        </button>
      </form>
      {error && <p data-testid="delivery-error" className="mt-3 text-sm text-amber-300">{error}</p>}
      {result && !error && <Result r={result} />}
      {result && !error && !result.available && item && <FreightQuote items={[item]} key={result.city.id} />}
    </div>
  );
};
